import React from 'react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    Cell,
    LabelList,
} from 'recharts';
import { GenericCard } from '@ui-components/Card/GenericCard';
import type { AccountBreakdown } from '../hooks/useFortiFlexData';

interface AccountDistributionChartProps {
    accounts: AccountBreakdown[];
}

const COLORS = ['#005E69', '#00BCD1', '#FAAEB0', '#761E21', '#f59e0b', '#3b82f6'];

function fmt(n: number | null | undefined): string {
    if (n === null || n === undefined) return 'Pending';
    return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

const AccountDistributionChart: React.FC<AccountDistributionChartProps> = ({
    accounts,
}) => {
    if (!accounts.length) {
        return (
            <GenericCard
                isCollapsible={false}
                variant="primary"
                className="mb-4"
                headerContent={
                    <h3 className="text-sm font-semibold text-semantic-text-primary">
                        Distribution by Account (Daily Consumption)
                    </h3>
                }
                bodyContent={
                    <p className="text-sm text-semantic-text-secondary">
                        No active account consumption data available.
                    </p>
                }
            />
        );
    }

    const data = accounts.map((a) => ({
        name: a.label,
        value: a.subtotalDaily,
        pct: a.pctOfTotal,
        cumulative: a.subtotalCumulative,
        monthly: a.subtotal30d,
        entitlements: a.entitlementCount,
    }));

    return (
        <GenericCard
            isCollapsible={false}
            variant="primary"
            className="mb-4"
            headerContent={
                <h3 className="text-sm font-semibold text-semantic-text-primary">
                    Distribution by Account (Daily Consumption)
                </h3>
            }
            bodyContent={
                <>
                    <ResponsiveContainer width="100%" height={accounts.length * 80 + 40}>
                        <BarChart data={data} layout="vertical" margin={{ left: 20, right: 80 }}>
                            <XAxis type="number" hide />
                            <YAxis
                                type="category"
                                dataKey="name"
                                width={150}
                                tick={{ fontSize: 12 }}
                            />
                            <Tooltip
                                formatter={(value: number | null) => [`${fmt(value)} pts`, 'daily']}
                                labelFormatter={(label: string) => label}
                            />
                            <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={28}>
                                {data.map((_, i) => (
                                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                                ))}
                                <LabelList
                                    dataKey="value"
                                    position="right"
                                    formatter={(v: number | null) =>
                                        v === null || v === undefined
                                            ? 'Pending'
                                            : `${fmt(v)} pts (${data.find((d) => d.value === v)?.pct ?? 0}%)`
                                    }
                                    style={{ fontSize: 11, fill: 'var(--color-semantic-text-secondary)' }}
                                />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>

                    <div className="flex justify-between mt-3 text-xs text-semantic-text-secondary border-t border-semantic-border-primary pt-3">
                        <div className="flex gap-4">
                            {accounts.map((a, i) => (
                                <div key={a.accountId} className="flex items-center gap-1.5">
                                    <span
                                        className="inline-block w-3 h-3 rounded-sm"
                                        style={{ backgroundColor: COLORS[i % COLORS.length] }}
                                    />
                                    <span>{a.label}</span>
                                </div>
                            ))}
                        </div>
                        <div>
                            Total:{' '}
                            {accounts.every((a) => a.subtotalDaily === null)
                                ? 'Pending'
                                : fmt(
                                      accounts.reduce(
                                          (s, a) => s + (a.subtotalDaily ?? 0),
                                          0
                                      )
                                  )}{' '}
                            pts/day |{' '}
                            {fmt(accounts.reduce((s, a) => s + a.subtotalCumulative, 0))} pts cumulative
                        </div>
                    </div>
                </>
            }
        />
    );
};

export default AccountDistributionChart;
